import "@fontsource-variable/outfit";
import "@fontsource-variable/manrope";
import "@fontsource-variable/jetbrains-mono";
import "@szyos/ui/tokens.css";
import "./styles/base.css";
import "./styles/app.css";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Outlet, RouterProvider, createRootRoute, createRoute, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";
import { api } from "./api";
import { ToastProvider } from "./components/feedback";
import { Shell } from "./components/Shell";
import { LiveProvider } from "./live";
import { Cockpit } from "./pages/Cockpit";
import { Agents, Decisions } from "./pages/Decisions";
import { IdeaStudio } from "./pages/IdeaStudio";
import { Ideas } from "./pages/Ideas";
import { Login } from "./pages/Login";
import { ProjectDetail, Projects } from "./pages/Projects";
import { Review } from "./pages/Review";
import { Usage } from "./pages/Usage";
import { WorkOrderPage } from "./pages/WorkOrder";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5_000, retry: 1, refetchOnWindowFocus: true } },
});

function useAuth() {
  return useQuery({
    queryKey: ["auth"],
    queryFn: api.authStatus,
    staleTime: 60_000,
    retry: 6,
    retryDelay: (attempt) => Math.min(4000, 500 * 2 ** attempt),
  });
}

function Root() {
  const auth = useAuth();
  if (auth.isLoading) return <p className="boot">szyOS startet …</p>;
  if (auth.isError || !auth.data) {
    return (
      <main className="login" id="main">
        <p className="form-error" role="alert">
          Server nicht erreichbar. Läuft der szyOS-Server?
        </p>
        <button type="button" className="btn" onClick={() => void auth.refetch()}>
          <span className="btn-label">Erneut versuchen</span>
        </button>
      </main>
    );
  }
  if (!auth.data.authenticated) return <Login status={auth.data} />;
  return (
    <LiveProvider>
      <a className="skip-link" href="#main">
        Zum Inhalt springen
      </a>
      <Shell auth={auth.data}>
        <Outlet />
      </Shell>
    </LiveProvider>
  );
}

function AuthGate({
  render,
}: {
  render: (auth: NonNullable<ReturnType<typeof useAuth>["data"]>) => React.ReactNode;
}) {
  const auth = useAuth();
  return auth.data ? <>{render(auth.data)}</> : null;
}

const rootRoute = createRootRoute({ component: Root });
const neuSearch = (s: Record<string, unknown>): { neu?: string } =>
  typeof s.neu === "string" ? { neu: s.neu } : {};

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => <AuthGate render={(a) => <Cockpit auth={a} />} />,
});
const ideasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ideas",
  validateSearch: neuSearch,
  component: IdeasRoute,
});
const ideaStudioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ideas/$ideaId",
  component: IdeaStudioRoute,
});
const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects",
  validateSearch: neuSearch,
  component: ProjectsRoute,
});
const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId",
  component: ProjectRoute,
});
const orderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work-orders/$orderId",
  component: OrderRoute,
});
const decisionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/decisions",
  component: Decisions,
});
const usageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/usage",
  component: Usage,
});
const reviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/review",
  component: Review,
});
const agentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents",
  component: () => <AuthGate render={(a) => <Agents auth={a} />} />,
});

function IdeasRoute() {
  const search = ideasRoute.useSearch();
  return <Ideas autoFocus={search.neu === "1"} />;
}
function IdeaStudioRoute() {
  const { ideaId } = ideaStudioRoute.useParams();
  return <IdeaStudio key={ideaId} ideaId={ideaId} />;
}
function ProjectsRoute() {
  const search = projectsRoute.useSearch();
  return <Projects autoFocus={search.neu === "1"} />;
}
function ProjectRoute() {
  const { projectId } = projectRoute.useParams();
  return <ProjectDetail projectId={projectId} />;
}
function OrderRoute() {
  const { orderId } = orderRoute.useParams();
  return <AuthGate render={(a) => <WorkOrderPage key={orderId} orderId={orderId} auth={a} />} />;
}

const routeTree = rootRoute.addChildren([
  indexRoute,
  ideasRoute,
  ideaStudioRoute,
  projectsRoute,
  projectRoute,
  orderRoute,
  decisionsRoute,
  agentsRoute,
  usageRoute,
  reviewRoute,
]);

const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById("root") as (HTMLElement & { __szyosRoot?: Root }) | null;
if (container) {
  // Bei Hot Reload im Entwicklungsmodus die bestehende Wurzel weiterverwenden.
  container.__szyosRoot ??= createRoot(container);
  container.__szyosRoot.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

// Installierbar als App (PWA): Der Service Worker speichert nichts außer der Offline-Seite (public/sw.js).
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      /* Ohne Service Worker läuft das Studio unverändert, nur nicht installierbar. */
    });
  });
}
